/* EB Cart Rewards — eb-cart-rewards.js v3
 *
 * Issue 1 fix: removed cart-update re-dispatch from _removeFreeGift.
 *   _removeFreeGift previously dispatched 'cart-update' after its API call.
 *   Our own component received it, ran _render a second time (~200ms later),
 *   resetting Gokwik's 700ms MutationObserver debounce timer a second time.
 *   The checkout button (rendered as disabled in fresh sidebar HTML) depends
 *   on Gokwik's timer firing to become enabled. Repeated timer resets kept it
 *   disabled indefinitely. Fix: update our UI only — do not re-dispatch.
 *
 * Issue 2 fix: smooth bar animation + staged milestone unlock animation.
 *   Tracks _prevSubtotal to detect newly-crossed milestones.
 *   Uses is-unlocking -> is-active class sequence with 250ms gap.
 *   Batches all DOM mutations in requestAnimationFrame (single observer tick).
 *
 * Issue 3 fix (2026-07-25): same root cause as Issue 1, different unpatched code path.
 *   _addFreeGift's success handler called _syncFromServer(), which re-ran the full
 *   _renderAnimated DOM-mutation batch even though adding a ₹0 gift item never changes
 *   total_price — a 100% redundant mutation landing right in Gokwik's 700ms window, at
 *   exactly the moment (crossing the free-gift threshold) high-value carts trigger it.
 *   Compounded by any other page-wide DOM activity (e.g. cart recommendations widget)
 *   also filling that window, the checkout button stayed disabled indefinitely.
 *   Fix: _renderAnimated now no-ops when the subtotal hasn't actually changed since the
 *   last render, so every call site (mount sync, cart-update handler, post-gift-add
 *   resync) only mutates the DOM when something is actually visually different.
 */
(function () {
  'use strict';

  var T1 = 49900;   // ₹499  → Free Shipping
  var T2 = 149900;  // ₹1499 → 10% OFF
  var T3 = 199900;  // ₹1999 → FREE Rose Water

  var MILESTONES = [T1, T2, T3];

  function fmt(paise) {
    return '₹' + Math.ceil(paise / 100);
  }

  function calcPct(subtotal) {
    if (subtotal <= 0)    return 0;
    if (subtotal < T1)    return (subtotal / T1) * 33.33;
    if (subtotal < T2)    return 33.33 + ((subtotal - T1) / (T2 - T1)) * 33.33;
    if (subtotal < T3)    return 66.66 + ((subtotal - T2) / (T3 - T2)) * 33.34;
    return 100;
  }

  function buildMsg(subtotal) {
    if (subtotal <= 0)    return 'Add items to unlock exclusive rewards.';
    if (subtotal < T1)    return 'Add <strong>' + fmt(T1 - subtotal) + '</strong> more to unlock <strong>FREE Shipping!</strong>';
    if (subtotal < T2)    return '🎉 <strong>FREE Shipping</strong> unlocked! Add <strong>' + fmt(T2 - subtotal) + '</strong> more for <strong>10% OFF.</strong>';
    if (subtotal < T3)    return '🎉 <strong>10% OFF</strong> unlocked! Add <strong>' + fmt(T3 - subtotal) + '</strong> more for a <strong>FREE Rose Water.</strong>';
    return '🎉 You\'ve unlocked <strong>all rewards!</strong> Enjoy your gifts.';
  }

  class EbCartRewards extends HTMLElement {
    connectedCallback() {
      this._giftVariantId = parseInt(this.dataset.giftVariantId || '0', 10);
      this._discountCode  = this.dataset.discountCode || '';
      this._busy          = false;
      this._prevSubtotal  = -1;
      this._rafId         = null;

      // Build the inner shell once on mount
      this.innerHTML = this._shell();

      // Cache DOM refs
      this._fill     = this.querySelector('.ebr__fill');
      this._msg      = this.querySelector('.ebr__message');
      this._icons    = this.querySelectorAll('.ebr__milestone');
      this._rewards  = this.querySelector('.ebr__all-rewards');
      this._nudge    = this.querySelector('.ebr__discount-nudge');

      // Instant initial render from server-rendered subtotal (no animation flash)
      var initSubtotal = parseInt(this.dataset.subtotal || '0', 10);
      this._renderInstant(initSubtotal);

      // Full cart.js sync on mount
      this._syncFromServer();

      // Listen for theme cart updates
      this._cartHandler = (e) => this._onCartUpdate(e.detail);
      document.addEventListener('cart-update', this._cartHandler);
    }

    disconnectedCallback() {
      document.removeEventListener('cart-update', this._cartHandler);
      if (this._rafId) cancelAnimationFrame(this._rafId);
    }

    /* ── Event handler ─────────────────────────────────────── */
    _onCartUpdate(cart) {
      if (!cart) return;
      var subtotal = cart.total_price || 0;
      this._renderAnimated(subtotal);
      this._handleFreeGift(cart, subtotal);
    }

    /* ── Server sync ───────────────────────────────────────── */
    _syncFromServer() {
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then((cart) => {
          var subtotal = cart.total_price || 0;
          this._renderAnimated(subtotal);
          this._handleFreeGift(cart, subtotal);
        })
        .catch(function() {});
    }

    /* ── Instant render (no CSS transition — for initial state) */
    _renderInstant(subtotal) {
      this._prevSubtotal = subtotal;
      var pct = calcPct(subtotal);

      // Disable transition, set width, force reflow, re-enable
      this._fill.style.transition = 'none';
      this._fill.style.width = Math.min(pct, 100) + '%';
      void this._fill.offsetWidth; // force reflow
      this._fill.style.transition = '';

      MILESTONES.forEach((threshold, i) => {
        if (this._icons[i]) {
          this._icons[i].classList.toggle('is-active', subtotal >= threshold);
        }
      });

      if (this._msg)     this._msg.innerHTML = buildMsg(subtotal);
      if (this._nudge)   this._nudge.hidden = !(subtotal >= T2 && this._discountCode);
      if (this._rewards) this._rewards.hidden = subtotal < T3;

      this._syncAriaProgressbar(pct, subtotal);
    }

    /* ── Animated render (smooth transition + milestone bounce) */
    _renderAnimated(subtotal) {
      // Issue 3 fix (2026-07-25): skip the whole DOM-mutation batch when the subtotal hasn't
      // actually changed since the last render. gokwik.liquid's checkout-enable script runs a
      // MutationObserver on the *entire* <body> with a 700ms debounce — the checkout button only
      // enables once 700ms pass with zero mutations anywhere on the page. Every call site here
      // (mount-time _syncFromServer, and especially _addFreeGift's post-add resync — adding a ₹0
      // gift item never changes total_price, so that resync was 100% redundant) was firing this
      // mutation batch even when nothing visually changed, repeatedly resetting Gokwik's timer and
      // leaving the checkout button stuck disabled — worse at high-value carts (gift threshold
      // triggers the extra resync) combined with any other page activity (e.g. cart
      // recommendations) filling the 700ms gap. See Issue 1 fix above for the original instance of
      // this same root cause.
      if (subtotal === this._prevSubtotal) return;
      var prev = this._prevSubtotal;
      this._prevSubtotal = subtotal;
      var pct = calcPct(subtotal);
      var msg = buildMsg(subtotal);

      // Detect which milestones are newly crossed
      var newlyUnlocked = [];
      MILESTONES.forEach(function(threshold, i) {
        if (prev < threshold && subtotal >= threshold) {
          newlyUnlocked.push(i);
        }
      });

      // Cancel any queued frame to avoid double-update
      if (this._rafId) cancelAnimationFrame(this._rafId);

      // Batch ALL DOM mutations into one rAF tick
      // This means MutationObserver fires only once per cart update
      this._rafId = requestAnimationFrame(() => {
        // 1. Progress fill — CSS transition handles smooth animation
        this._fill.style.width = Math.min(pct, 100) + '%';

        // 2. Milestones
        MILESTONES.forEach((threshold, i) => {
          var el = this._icons[i];
          if (!el) return;
          var isActive = subtotal >= threshold;
          var isNew    = newlyUnlocked.indexOf(i) !== -1;

          if (isNew) {
            // Stage: is-unlocking fires the bounce animation (green colour, scale 0.8 → 1.2)
            el.classList.remove('is-active', 'is-unlocking');
            void el.offsetWidth; // force reflow so animation restarts
            el.classList.add('is-unlocking');

            // 250ms pause → swap to is-active (checkmark, steady glow)
            setTimeout(() => {
              el.classList.remove('is-unlocking');
              el.classList.add('is-active');
            }, 250);

          } else {
            el.classList.toggle('is-active', isActive);
            el.classList.remove('is-unlocking');
          }
        });

        // 3. Message
        if (this._msg) this._msg.innerHTML = msg;

        // 4. All-rewards summary
        if (this._rewards) this._rewards.hidden = subtotal < T3;

        // 5. Discount nudge
        if (this._nudge) this._nudge.hidden = !(subtotal >= T2 && this._discountCode);

        // 6. ARIA
        this._syncAriaProgressbar(pct, subtotal);
      });
    }

    _syncAriaProgressbar(pct, subtotal) {
      var track = this.querySelector('.ebr__track');
      if (track) {
        track.setAttribute('aria-valuenow', Math.round(pct));
        track.setAttribute('aria-valuetext', buildMsg(subtotal).replace(/<[^>]+>/g, ''));
      }
    }

    /* ── Free gift handling ────────────────────────────────── */
    _handleFreeGift(cart, subtotal) {
      if (!this._giftVariantId || this._busy) return;

      var items = (cart && cart.items) ? cart.items : [];
      var giftLine = null;
      items.forEach((item) => {
        if (item.variant_id == this._giftVariantId) giftLine = item;
      });

      var shouldHaveGift = subtotal >= T3;
      var hasGift        = !!giftLine;

      if (shouldHaveGift && !hasGift) {
        this._addFreeGift();
      } else if (!shouldHaveGift && hasGift) {
        this._removeFreeGift(giftLine.key);
      }
    }

    _addFreeGift() {
      this._busy = true;
      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id:         this._giftVariantId,
          quantity:   1,
          properties: { _eb_free_gift: 'true', _eb_free_gift_price: '0' }
        })
      })
      .then(function(r) { return r.json(); })
      .then(() => {
        this._busy = false;
        // Silently sync our component state only
        // The theme will refresh the sidebar on the next user interaction
        this._syncFromServer();
      })
      .catch(() => { this._busy = false; });
    }

    _removeFreeGift(lineKey) {
      this._busy = true;
      fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      })
      .then(function(r) { return r.json(); })
      .then((cart) => {
        this._busy = false;
        // ⚠️ IMPORTANT: do NOT dispatch 'cart-update' here.
        // Doing so triggers our own listener again → second _render batch (~200ms later)
        // → resets Gokwik's 700ms MutationObserver debounce → checkout button
        // stays in initial `disabled` state indefinitely.
        // Instead, update ONLY our own component UI.
        var subtotal = cart.total_price || 0;
        this._renderAnimated(subtotal);
      })
      .catch(() => { this._busy = false; });
    }

    /* ── Shell HTML ────────────────────────────────────────── */
    _shell() {
      return `<div class="ebr">
  <div class="ebr__track-wrap">
    <div class="ebr__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
      <div class="ebr__fill"></div>
    </div>
    <div class="ebr__milestones" aria-hidden="true">
      <button class="ebr__milestone ebr__milestone--1" tabindex="-1" type="button">
        <span class="ebr__milestone-icon">
          <svg class="ebr__ico-lock" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v10H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/></svg>
          <svg class="ebr__ico-check" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </span>
        <span class="ebr__milestone-label">Free<br>Shipping</span>
      </button>
      <button class="ebr__milestone ebr__milestone--2" tabindex="-1" type="button">
        <span class="ebr__milestone-icon">
          <svg class="ebr__ico-lock" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>
          <svg class="ebr__ico-check" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </span>
        <span class="ebr__milestone-label">10%<br>OFF</span>
      </button>
      <button class="ebr__milestone ebr__milestone--3" tabindex="-1" type="button">
        <span class="ebr__milestone-icon">
          <svg class="ebr__ico-lock" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v10H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
          <svg class="ebr__ico-check" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </span>
        <span class="ebr__milestone-label">FREE<br>Rose Water</span>
      </button>
    </div>
  </div>

  <p class="ebr__message" role="status" aria-live="polite"></p>

  <div class="ebr__all-rewards" hidden>
    <span class="ebr__reward-chip ebr__reward-chip--done">✓ FREE Shipping</span>
    <span class="ebr__reward-chip ebr__reward-chip--done">✓ 10% Discount</span>
    <span class="ebr__reward-chip ebr__reward-chip--done">✓ FREE Rose Water</span>
  </div>

  <div class="ebr__discount-nudge" hidden>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
    <span>Your <strong>10% OFF</strong> discount will be applied automatically at checkout.</span>
  </div>
</div>`;
    }
  }

  if (!customElements.get('eb-cart-rewards')) {
    customElements.define('eb-cart-rewards', EbCartRewards);
  }
})();
